import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Button from 'components/base/Button';
import PageBreadcrumb from 'components/common/PageBreadcrumb';
import SearchBox from 'components/common/SearchBox';
import { ChangeEvent, useMemo, useState } from 'react';
import { Alert, Col, Row } from 'react-bootstrap';
import {
  faExclamationTriangle,
  faPlus
} from '@fortawesome/free-solid-svg-icons';
import { useQuery } from 'react-query';
import { GET_CUSTOMERS_QUERY } from 'services/api/customer';
import { customerTableColumns } from 'components/tables/CustomerTable';
import { CustomerViewBreadcrumbItems } from 'data/commonData';
import { Link } from 'react-router-dom';
import { SITE_ENDPOINT } from 'Constant';
import GTable from 'components/tables/GTable';
import { PERMISSIONS } from 'constants/permissions';
import { usePermissions } from 'hooks/usePermissions';

const ViewCustomer = () => {
  const [searchTerm, setSearchTerm] = useState<string>('');
  const {
    isLoading,
    isError,
    data: customers
  } = useQuery('get_customer', GET_CUSTOMERS_QUERY, {
    staleTime: 3 * 60 * 60 * 1000
  });

  const filteredCustomers = useMemo(() => {
    if (!customers?.data) return [];

    return (
      customers.data
        .filter(customer => {
          const searchStr = searchTerm.toLowerCase();
          return (
            customer?.organizationName?.toLowerCase().includes(searchStr) ||
            customer?.phoneNumber?.toLowerCase().includes(searchStr) ||
            customer?.address?.toLowerCase().includes(searchStr) ||
            customer?.country?.toLowerCase().includes(searchStr) ||
            customer?.customerRepresentative?.firstName
              ?.toLowerCase()
              .includes(searchStr) ||
            customer?.customerRepresentative?.lastName
              ?.toLowerCase()
              .includes(searchStr)
          );
        })
        .sort((a: any, b: any) => {
          return a?.organizationName?.localeCompare(b?.organizationName ?? '');
        }) ?? []
    );
  }, [customers?.data, searchTerm]);

  const handleSearchInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const [canCreateCustomer, canEditCustomer, canDeleteCustomer] =
    usePermissions([
      PERMISSIONS.CUSTOMER.COMPANY.CREATE,
      PERMISSIONS.CUSTOMER.COMPANY.UPDATE,
      PERMISSIONS.CUSTOMER.COMPANY.DELETE
    ]);

  const customerColumns = useMemo(
    () => customerTableColumns(canEditCustomer, canDeleteCustomer),
    [canEditCustomer, canDeleteCustomer]
  );

  return (
    <div>
      <PageBreadcrumb items={CustomerViewBreadcrumbItems} />
      <div className="mb-9">
        <h2 className="mb-5">All Company</h2>

        {isError && (
          <Alert variant="danger">
            <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
            Error fetching customers
          </Alert>
        )}

        <div className="mb-4">
          <Row className="g-3">
            <Col xs="auto">
              <SearchBox
                placeholder="Search Company"
                onChange={handleSearchInputChange}
                value={searchTerm}
              />
            </Col>
            <Col
              xs="auto"
              className="scrollbar overflow-hidden-y flex-grow-1"
            ></Col>
            {canCreateCustomer && (
              <Col xs="auto">
                <Link
                  to={`${SITE_ENDPOINT.BASE}${SITE_ENDPOINT.COMPANY.CREATE_COMPANY}`}
                >
                  <Button variant="primary">
                    <FontAwesomeIcon icon={faPlus} className="me-2" />
                    Add Company
                  </Button>
                </Link>
              </Col>
            )}
          </Row>
        </div>

        <div className="mx-n4 px-4 mx-lg-n6 px-lg-6 bg-white border-top border-bottom border-200 position-relative top-1">
          <GTable
            data={filteredCustomers}
            columns={customerColumns}
            isLoading={isLoading}
          />
        </div>
      </div>
    </div>
  );
};

export default ViewCustomer;
